import { PageWrapper } from '@components'
import { LABELS, TOOLTIPS } from '@constants'
import { useIsMobile } from '@hooks/useIsMobile'
import { useStore } from '@hooks/useStore'
import { Add, Delete, Edit } from '@mui/icons-material'
import {
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material'
import { ISingleProduct } from '@typings'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { ProductApi } from 'src/api/product.api'
import { ProductFormModal } from 'src/partials/modals/products/productForm.modal'

export const ProductList = observer(() => {
  const { company, ui } = useStore()
  const [products, setProducts] = useState<ISingleProduct[]>([])
  const [loading, setLoading] = useState(true)
  const isMobile = useIsMobile()
  const productApi = new ProductApi()
  const [modalOpen, setModalOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<ISingleProduct | null>(null)

  useEffect(() => {
    loadProducts()
  }, [])

  const loadProducts = async () => {
    try {
      setLoading(true)
      const data = await productApi.getAll()
      setProducts(data)
    } catch (error) {
      console.error('Failed to load products:', error)
      toast.error('Fout bij het laden van producten')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateProduct = () => {
    setEditingProduct(null)
    setModalOpen(true)
  }

  const handleEdit = (e: React.MouseEvent, product: ISingleProduct) => {
    e.stopPropagation()
    setEditingProduct(product)
    setModalOpen(true)
  }

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    try {
      await ui.confirm({
        title: 'Product verwijderen?',
        content: 'Weet je zeker dat je dit product wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
        withCancel: true,
      })

      await productApi.delete(id)
      toast.success('Product succesvol verwijderd')
      loadProducts()
    } catch (error: any) {
      // User cancelled or deletion failed
      if (error?.response) {
        console.error('Failed to delete product:', error)
        toast.error(error?.response?.data?.message || 'Fout bij het verwijderen van product')
      }
    }
  }

  const handleSave = async (productData: Partial<ISingleProduct>) => {
    try {
      if (editingProduct) {
        await productApi.update(editingProduct.id, productData)
        toast.success('Product succesvol bijgewerkt')
      } else {
        if (!company.current_company?.id) {
          toast.error('Bedrijfsinformatie niet beschikbaar. Probeer de pagina te vernieuwen.')
          throw new Error('Company ID is not available')
        }
        const createData: Partial<ISingleProduct> = {
          ...productData,
          company_id: company.current_company.id,
        }
        await productApi.create(createData)
        toast.success('Product succesvol aangemaakt')
      }
      loadProducts()
    } catch (error: any) {
      console.error('Failed to save product:', error)
      toast.error(error?.response?.data?.message || 'Fout bij het opslaan van product')
      throw error
    }
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setEditingProduct(null)
  }

  if (loading) {
    return (
      <PageWrapper pageTitle={LABELS.CATALOG}>
        <CircularProgress />
      </PageWrapper>
    )
  }

  return (
    <PageWrapper
      pageTitle={LABELS.CATALOG}
      actions={
        <Tooltip title={TOOLTIPS.CREATE_NEW_PRODUCT}>
          <Button
            endIcon={<Add />}
            variant="contained"
            onClick={handleCreateProduct}>
            Nieuw
          </Button>
        </Tooltip>
      }>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{LABELS.TITLE}</TableCell>
              <TableCell>{LABELS.EXTRA_DESCRIPTION}</TableCell>
              <TableCell align="right">{LABELS.UNIT_PRICE}</TableCell>
              <TableCell align="right">Acties</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Geen producten gevonden
                </TableCell>
              </TableRow>
            ) : (
              products.map(product => (
                <TableRow
                  key={product.id}
                  hover>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>{product.description || '-'}</TableCell>
                  <TableCell align="right">{product.formatted_price || '-'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={TOOLTIPS.EDIT_PRODUCT}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => handleEdit(e, product)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={TOOLTIPS.DELETE_PRODUCT}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => handleDelete(e, product.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ProductFormModal
        open={modalOpen}
        product={editingProduct}
        onClose={handleCloseModal}
        onSave={handleSave}
      />
    </PageWrapper>
  )
})
